import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import Publish from '@mui/icons-material/Publish';
import Delete from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { fetchPosts, updatePost, deletePost } from '../../store/slices/postsSlice';
import { getCategoryLabel } from '../../utils/categoryLabels';

export default function PendingPosts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.auth);
  const { list: posts, loading } = useSelector((s) => s.posts);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'moderator') dispatch(fetchPosts());
  }, [dispatch, user?.role]);

  if (user?.role !== 'admin' && user?.role !== 'moderator') return <Navigate to="/dashboard" replace />;

  const pending = posts.filter((p) => p.status === 'inConsultation');
  const q = searchQuery.toLowerCase().trim();
  const filtered = !q
    ? pending
    : pending.filter((p) => {
        const title = (p.title || '').toLowerCase();
        const author = (p.author?.fullname || p.author || '').toLowerCase();
        const cat = (getCategoryLabel(p.category) || '').toLowerCase();
        return title.includes(q) || author.includes(q) || cat.includes(q);
      });

  const handlePublish = async (id) => {
    setActionLoadingId(id);
    const result = await dispatch(updatePost({ id, status: 'published' }));
    setActionLoadingId(null);
    if (updatePost.fulfilled.match(result)) {
      enqueueSnackbar('Article publié.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    setActionLoadingId(id);
    const result = await dispatch(deletePost(id));
    setActionLoadingId(null);
    if (deletePost.fulfilled.match(result)) {
      enqueueSnackbar('Article supprimé.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>En consultation</Typography>
      <TextField
        size="small"
        placeholder="Rechercher par titre, auteur, catégorie…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, minWidth: 280, maxWidth: 400 }}
      />
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Titre</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Auteur</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {pending.length === 0 ? 'Aucun article en consultation.' : 'Aucun résultat pour cette recherche.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow key={p._id}>
                  <TableCell>{p.title}</TableCell>
                  <TableCell>{getCategoryLabel(p.category)}</TableCell>
                  <TableCell>{p.author?.fullname || p.author}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<Edit />} onClick={() => navigate(`/dashboard/posts/edit/${p._id}`)} disabled={!!actionLoadingId}>Modifier</Button>
                    <IconButton size="small" onClick={() => handlePublish(p._id)} disabled={!!actionLoadingId} title="Publier">
                      {actionLoadingId === p._id ? <CircularProgress size={20} /> : <Publish />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(p._id)} disabled={!!actionLoadingId}>
                      {actionLoadingId === p._id ? <CircularProgress size={20} /> : <Delete />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
}
